import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import HistoricalSection from "./HistoricalSection";
import DataService from "../../services/DataService";

import { useUser } from "../../context/userContext";
import { useDot } from "../../context/automatonContext";
import { useAlert } from "../../context/Feedback/alertContext";
import { makeStyles } from "@material-ui/core/styles";
import styles from "../../assets/jss/material-dashboard-react/components/dialogHistorical";
import { Button } from "@material-ui/core";
import constants from "../../constants/tutorialAutomaton";
import historicDialogConstants from "../../constants/Dialogs/historicalDialogConstants";
import "./classes.css";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";

import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";

import { MACHINE_TYPES } from "../../variables/machineVariables";
import { isNullOrUndefined } from "../../utils/generalValidators";

const useStyles = makeStyles(styles);

/**
 * Componente que exibe o diálogo histórico de autômatos criados pelo usuário
 *
 * @param {Object} props Propriedades passadas para o componente.
 * @returns {JSX.Element} Componente JSX.
 */
export default function HistoricalDialog(props) {
  const { language, user, setUser, credential } = useUser();
  const { setAlert } = useAlert();
  const {
    setDot,
    setDotName,
    createdDate,
    setCreatedDate,
    isActiveDialogHistorical,
    setActiveDialogHistorical,
    setArrayElements,
    setMachineStates,
    setMachineTransitions,
    setAutomatonType,
  } = useDot();

  const [checked, setChecked] = useState(true);
  const [historicalToday, setHistoricalToday] = useState([]);
  const [historicalYesterday, setHistoricalYesterday] = useState([]);
  const [historicalLastWeeks, setHistoricalLastWeeks] = useState([]);
  const [historicalMachineSorted, setHistoricalMachineSorted] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [uniqueTypes, setUniqueTypes] = useState([]);

  const classes = useStyles();

  const handleClickMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  /**
   * Função para exclusão em massa por período
   * @param {number} months Quantidade de meses (1, 6, 12)
   */
  const handleBulkDelete = async (months) => {
    handleCloseMenu();

    const confirmMessage =
      historicDialogConstants.clear.bulkDelete.title[language];
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await DataService.deleteMachineOlderThan(
        user.email,
        months,
        credential
      );

      if (response && response.data) {
        setUser({ ...user, historicalMachine: response.data });

        if (createdDate) {
          const exists = response.data.some(
            (h) => h.createdDate === createdDate
          );
          if (!exists) {
            await clearMachine();
            setCreatedDate(null);
            setDotName(null);
          }
        }

        setAlert({
          active: true,
          text: historicDialogConstants.clear.bulkDelete.alert.success[
            language
          ],
          color: "success",
          severity: "success",
        });
      }
    } catch (error) {
      setAlert({
        active: true,
        text: historicDialogConstants.clear.bulkDelete.alert.error[language],
        color: "danger",
        severity: "error",
      });
    }
  };

  /**
   * Ordena o histórico com base no parâmetro.
   *
   * @param {Array} historical Histórico de máquinas.
   * @param {boolean} isAscending Define o padrão da ordenação. Se true é Ascendente e false Descendente
   * @returns {Array} Histórico ordenado.
   */
  const sortHistorical = (historical, isAscending) => {
    return historical?.slice().sort((a, b) => {
      const dateA = new Date(a.lastUpdatedDate); // Converte o LocalDateTime para Date
      const dateB = new Date(b.lastUpdatedDate); // Converte o LocalDateTime para Date

      return isAscending ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Atualiza os históricos separados por períodos, padrão de 3 períodos. (HOJE, ONTEM, ÚLTIMAS SEMANAS)
   *
   * @param {Array} sortedHistorical Histórico ordenado.
   */
  const updateHistoricalPeriods = (sortedHistorical) => {
    const today = new Date();
    const tempToday = [];
    const tempYesterday = [];
    const tempLastWeeks = [];

    const filteredHistorical = sortedHistorical.filter((element) => {
      let type;
      if (isNullOrUndefined(element.automatonType)) {
        type = MACHINE_TYPES.AF;
      } else {
        type = element.automatonType;
      }
      return !filterType || type === filterType;
    });

    filteredHistorical.forEach((element) => {
      let mcToHtml = toDOTSimple(element.variables);
      let testMcLength = mcToHtml.split("\n");
      if (testMcLength.length > 2) {
        mcToHtml = testMcLength[0] + "\n" + testMcLength[1] + "\n ...";
      }

      const date = new Date(element.lastUpdatedDate);
      const formattedDate = `${date.getDate()}/${
        date.getMonth() + 1
      }/${date.getFullYear()} - ${date.getHours()}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;

      let automatonTypeValue;
      if (isNullOrUndefined(element.automatonType)) {
        automatonTypeValue = MACHINE_TYPES.AF;
      } else {
        automatonTypeValue = element.automatonType;
      }

      const obj = {
        name: element.name,
        value: mcToHtml,
        date: formattedDate,
        createdDate: element.createdDate,
        automatonType: automatonTypeValue,
      };

      if (isToday(element, today)) {
        tempToday.push(obj);
      } else if (isYesterday(element, today)) {
        tempYesterday.push(obj);
      } else {
        tempLastWeeks.push(obj);
      }
    });

    setHistoricalToday(tempToday);
    setHistoricalYesterday(tempYesterday);
    setHistoricalLastWeeks(tempLastWeeks);
  };

  /**
   * Verifica se a data é hoje.
   *
   * @param {Object} element Elemento do histórico.
   * @param {Date} today Data atual.
   * @returns {boolean} Verdadeiro se a data for hoje.
   */
  const isToday = (element, today) =>
    today.getDate() === new Date(element.lastUpdatedDate).getDate() &&
    today.getMonth() === new Date(element.lastUpdatedDate).getMonth() &&
    today.getFullYear() === new Date(element.lastUpdatedDate).getFullYear();

  /**
   * Verifica se a data é ontem.
   *
   * @param {Object} element Elemento do histórico.
   * @param {Date} today Data atual.
   * @returns {boolean} Verdadeiro se a data for ontem.
   */
  const isYesterday = (element, today) =>
    today.getDate() - 1 === new Date(element.lastUpdatedDate).getDate() &&
    today.getMonth() === new Date(element.lastUpdatedDate).getMonth() &&
    today.getFullYear() === new Date(element.lastUpdatedDate).getFullYear();

  /**
   * Simplifica a representação do autômato em DOT.
   *
   * @param {string} dot Representação do autômato em DOT.
   * @returns {string} Representação simplificada.
   */
  const toDOTSimple = (Dot) => {
    try {
      let dot = "";
      const completeRules = Dot.split("\n\n");
      const completeTransitions = completeRules[1];

      if (!isNullOrUndefined(completeTransitions)) {
        const arrayTransitions = completeTransitions.split("\n");

        for (let i = 0; i < arrayTransitions.length; i++) {
          dot +=
            arrayTransitions[i].split("label")[0] +
            arrayTransitions[i].split('"')[1] +
            "]\n";
        }
      }

      let importantStates = Dot.split("\n\n")[1];

      importantStates = importantStates.split("\n}")[0];

      const arrayImportantStates = importantStates.split("\n");

      for (let i = 0; i < arrayImportantStates.length; i++) {
        dot += arrayImportantStates[i].split("[")[0] + "=";
        if (arrayImportantStates[i].split("=")[1].split("]")[0] === "diamond") {
          dot += "initial\n";
        } else if (
          arrayImportantStates[i].split("=")[1].split("]")[0] === "square"
        ) {
          dot += "final\n";
        } else if (
          arrayImportantStates[i].split("=")[1].split("]")[0] === "Mdiamond"
        ) {
          dot += "initial/final\n";
        }
      }

      return dot;
    } catch (e) {
      alert(e);
    }
  };

  const deleteHistoricalAutomaton = async (index, time) => {
    let itemToDelete = null;

    if (time === "today") {
      itemToDelete = historicalToday[index];
    } else if (time === "yesterday") {
      itemToDelete = historicalYesterday[index];
    } else if (time === "lastWeeks") {
      itemToDelete = historicalLastWeeks[index];
    }

    if (!itemToDelete || !itemToDelete.createdDate) {
      return;
    }

    try {
      const response = await DataService.deleteHistoricalMc(
        user.email,
        itemToDelete.createdDate,
        credential
      );

      if (response && response.data) {
        setUser({ ...user, historicalMachine: response.data });

        if (createdDate === itemToDelete.createdDate) {
          await clearMachine();
          setCreatedDate(null);
          setDotName(null);
        }
      }
    } catch (error) {
      setAlert({
        active: true,
        text: historicDialogConstants.clear.alert.error[language],
        color: "danger",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    if (user.historicalMachine) {
      const sortedHistorical = sortHistorical(user.historicalMachine, false);
      setHistoricalMachineSorted(sortedHistorical);

      const types = new Set();
      user.historicalMachine.forEach((item) => {
        let type;
        if (isNullOrUndefined(item.automatonType)) {
          type = MACHINE_TYPES.AF;
        } else {
          type = item.automatonType;
        }
        types.add(type);
      });
      setUniqueTypes(Array.from(types));
    }
  }, [user.historicalMachine]);

  useEffect(() => {
    if (historicalMachineSorted) {
      updateHistoricalPeriods(historicalMachineSorted);
    }
  }, [historicalMachineSorted, filterType]);

  /**
   * Fecha o diálogo.
   */
  const handleClose = () => {
    setActiveDialogHistorical(false);
  };

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  /**
   * Renderiza o autômato do histórico.
   *
   * @param {number} index Índice do histórico.
   * @param {string} time Período do histórico (today, yesterday, lastWeeks).
   */
  const drawHistoricalAutomaton = async (index, time) => {
    const filteredAndSortedHistorical = historicalMachineSorted.filter(
      (element) => {
        let type;
        if (isNullOrUndefined(element.automatonType)) {
          type = MACHINE_TYPES.AF;
        } else {
          type = element.automatonType;
        }
        return !filterType || type === filterType;
      }
    );

    if (time === "yesterday") {
      index += historicalToday.length;
    } else if (time === "lastWeeks") {
      index += historicalToday.length + historicalYesterday.length;
    }

    const dot = filteredAndSortedHistorical[index];
    await clearMachine();

    if (isNullOrUndefined(dot.automatonType)) {
      setAutomatonType(MACHINE_TYPES.AF);
    } else {
      setAutomatonType(dot.automatonType);
    }
    setDotName(dot.name);
    setCreatedDate(dot.createdDate);
    setDot(dot.variables);
    setActiveDialogHistorical(false);
  };

  /**
   * Limpa os estados relacionados ao autômato para preparar o ambiente
   * para o desenho de um novo autômato. Essa função redefine variáveis
   * globais e propriedades relacionadas ao desenho do autômato.
   *
   * @async
   * @function clearMachine
   * @returns {Promise<void>} Uma promessa que é resolvida após todos os
   * estados terem sido redefinidos.
   */
  async function clearMachine() {
    return new Promise((resolve) => {
      props.setStars([]);
      setDot("");
      setAutomatonType(null);
      setMachineStates([]);
      setMachineTransitions([]);
      setArrayElements([]);
      props.setChilds([]);
      resolve();
    });
  }

  return (
    <div>
      <Dialog
        checked={checked}
        onChange={handleChange}
        open={isActiveDialogHistorical}
        keepMounted
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle className={classes.dialog} id="alert-dialog-slide-title">
          <div className="wrapper-title">
            {/* Lado Esquerdo: Título e Filtro (Develop) */}
            <div style={{ display: "flex", alignItems: "center" }}>
              {constants.historic[language]}

              <FormControl
                className={classes.formControl}
                style={{ marginLeft: 20, minWidth: 120 }}
              >
                <InputLabel
                  id="filter-label"
                  htmlFor="filter-select"
                  shrink
                ></InputLabel>
                <Select
                  labelId="filter-label"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  displayEmpty
                  inputProps={{
                    id: "filter-select",
                    name: "filter_type",
                  }}
                >
                  <MenuItem value="">{constants.all[language]}</MenuItem>
                  {uniqueTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Lado Direito: Exclusão em Massa (HEAD) e Fechar */}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconButton
                aria-controls="bulk-delete-menu"
                aria-haspopup="true"
                onClick={handleClickMenu}
                style={{ padding: 8 }}
                title={historicDialogConstants.clear.title[language]}
              >
                <DeleteSweepIcon fontSize="large" />
              </IconButton>

              <Menu
                id="bulk-delete-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
              >
                <MenuItem disabled style={{ opacity: 0.7, fontWeight: "bold" }}>
                  {historicDialogConstants.clear.inputLabel[language]}
                </MenuItem>
                <MenuItem onClick={() => handleBulkDelete(1)}>
                  <DeleteSweepIcon
                    style={{ marginRight: 10, color: "#f44336" }}
                  />{" "}
                  1 {historicDialogConstants.clear.period.month[language]}
                </MenuItem>
                <MenuItem onClick={() => handleBulkDelete(6)}>
                  <DeleteSweepIcon
                    style={{ marginRight: 10, color: "#f44336" }}
                  />{" "}
                  6 {historicDialogConstants.clear.period.months[language]}
                </MenuItem>
                <MenuItem onClick={() => handleBulkDelete(12)}>
                  <DeleteSweepIcon
                    style={{ marginRight: 10, color: "#f44336" }}
                  />{" "}
                  1 {historicDialogConstants.clear.period.year[language]}
                </MenuItem>
              </Menu>

              <div className="wrapper-button-close button-margin">
                <Button
                  style={{
                    backgroundColor: "#999",
                    color: "#FFF",
                    marginLeft: 8,
                  }}
                  onClick={handleClose}
                >
                  X
                </Button>
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialog}>
          {historicalToday.length > 0 && (
            <HistoricalSection
              title={constants.today[language]}
              data={historicalToday}
              onButtonClick={(index) => drawHistoricalAutomaton(index, "today")}
              onDeleteClick={(index) =>
                deleteHistoricalAutomaton(index, "today")
              }
            />
          )}
          {historicalYesterday.length > 0 && (
            <HistoricalSection
              title={constants.yesterday[language]}
              data={historicalYesterday}
              onButtonClick={(index) =>
                drawHistoricalAutomaton(index, "yesterday")
              }
              onDeleteClick={(index) =>
                deleteHistoricalAutomaton(index, "yesterday")
              }
            />
          )}
          {historicalLastWeeks.length > 0 && (
            <HistoricalSection
              title={constants.lastWeeks[language]}
              data={historicalLastWeeks}
              onButtonClick={(index) =>
                drawHistoricalAutomaton(index, "lastWeeks")
              }
              onDeleteClick={(index) =>
                deleteHistoricalAutomaton(index, "lastWeeks")
              }
            />
          )}
        </DialogContent>
        <DialogActions className={classes.dialog}></DialogActions>
      </Dialog>
    </div>
  );
}
